import { useEffect, useState } from "react"
import { IconEyeFill18 } from "nucleo-ui-fill-18"

import { Switch } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useRole } from "@/lib/useRole"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"

export function ShadowModeCard({
	agentEnabled,
	shadowRepliesEnabled,
	workspaceId,
}: {
	agentEnabled: boolean
	shadowRepliesEnabled: boolean | undefined
	workspaceId?: string
}) {
	const { role } = useRole()
	const stopMutation = api.shadowReplies.stopPreviews.useMutation()
	const resumeMutation = api.shadowReplies.resumePreviews.useMutation()
	const [enabled, setEnabled] = useState(shadowRepliesEnabled !== false)

	useEffect(() => {
		setEnabled(shadowRepliesEnabled !== false)
	}, [shadowRepliesEnabled])

	const isAdmin = role === "ADMIN"
	const blocked = !agentEnabled || !isAdmin

	const explainBlock = () => {
		if (!agentEnabled) {
			toast.info({
				title: "Turn on the agent to use shadow mode",
				description:
					"Shadow mode drafts replies with the agent, so it runs while the agent is on.",
				icon: IconEyeFill18,
			})
			return
		}
		toast.info({
			title: "Ask an admin to change shadow mode",
			description: "Only workspace admins can change this setting.",
			icon: IconEyeFill18,
		})
	}

	const handleChange = async (next: boolean) => {
		setEnabled(next)
		try {
			await (next ? resumeMutation : stopMutation).mutateAsync()
			toast.success({
				title: next ? "Shadow mode is on" : "Shadow mode is off",
				icon: IconEyeFill18,
			})
		} catch (error) {
			setEnabled(!next)
			captureError(error, {
				domain: "ai-chat",
				action: next ? "resume shadow replies" : "stop shadow replies",
				workspaceId,
			})
			toast.error({
				title: "Couldn't update shadow mode",
				description: error instanceof Error ? error.message : undefined,
				icon: IconEyeFill18,
			})
		}
	}

	return (
		<SettingsContainer>
			<SettingItem
				title="Shadow mode"
				description="The agent drafts a reply on every channel where auto-reply is off. Your team reviews each one, then sends or declines it."
				action={
					<div role="presentation" onClick={blocked ? explainBlock : undefined}>
						<Switch
							name="shadowRepliesEnabled"
							label={null}
							checked={agentEnabled && enabled}
							disabled={blocked}
							className={blocked ? "pointer-events-none" : undefined}
							onCheckedChange={(next) => void handleChange(next)}
						/>
					</div>
				}
			/>
		</SettingsContainer>
	)
}
